/**
 * Component that displays the hierarchical tree structure of equipment.
 */

import React, { useEffect, useState, useCallback } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Loader from '@amzn/meridian/loader';
import SearchField from "@amzn/meridian/search-field";
import Input from "@amzn/meridian/input";
import { useEquipmentData, useSiteChange } from '../../../hooks/use-equipment-state';
import { EquipmentTreeSideMenu } from './equipment-tree-sidemenu';
import { EquipmentNode } from "../../../services/equipment";

interface EquipmentTreePanelProps {
  site: string;
  apmId: string;
  selectedNodeId: string | null;
  onNodeSelect: (node: EquipmentNode) => void;
}

export const EquipmentTreePanel: React.FC<EquipmentTreePanelProps> = ({
  site,
  apmId,
  selectedNodeId,
  onNodeSelect,
}) => {

  // Get equipment data
  const { equipmentHierarchy, isLoading, errors } = useEquipmentData();

  // Get site change functionality
  const {
    currentSite,
    siteChangeInProgress,
    siteChangeError,
    siteChangeSuccess,
    changeSite,
    validateSite
  } = useSiteChange();

  // Local state for site input and validation
  const [siteInputValue, setSiteInputValue] = useState(site);
  const [localValidationError, setLocalValidationError] = useState<string | null>(null);
  const [query, setQuery] = useState("")
  const onSubmit = () => console.log("search term:", query)

  // Update local input value when site prop changes
  useEffect(() => {
    setSiteInputValue(site);
    setLocalValidationError(null);
  }, [site]);

  // Clear local validation error when site change error is cleared
  useEffect(() => {
    if (!siteChangeError) {
      setLocalValidationError(null);
    }
  }, [siteChangeError]);

  // Handle site input change with real-time validation
  const handleSiteInputChange = useCallback((value: string) => {
    setSiteInputValue(value);

    // Clear previous local validation error
    setLocalValidationError(null);

    // Perform real-time validation if value is not empty
    if (value.trim()) {
      const validation = validateSite(value.trim());
      if (!validation.isValid) {
        setLocalValidationError(validation.error || 'Invalid site format');
        return; // Don't trigger site change if validation fails
      }
    }

    // Trigger debounced site change only if validation passes
    changeSite(value);
  }, [changeSite, validateSite]);

  // Handle site input submit (Enter key or blur)
  const handleSiteInputSubmit = useCallback(() => {
    const trimmedValue = siteInputValue.trim();

    if (!trimmedValue) {
      setLocalValidationError('Site cannot be empty');
      return;
    }

    // Validate before submitting
    const validation = validateSite(trimmedValue);
    if (!validation.isValid) {
      setLocalValidationError(validation.error || 'Invalid site format');
      return;
    }

    // Clear local validation error and submit
    setLocalValidationError(null);
    changeSite(trimmedValue);
  }, [siteInputValue, changeSite, validateSite]);

  // Handle site input blur
  const handleSiteInputBlur = useCallback(() => {
    handleSiteInputSubmit();
  }, [handleSiteInputSubmit]);



  // Get the appropriate error message to display
  const getErrorMessage = useCallback(() => {
    if (localValidationError) {
      return localValidationError;
    }
    if (siteChangeError) {
      // Provide more specific error messages based on error type
      if (siteChangeError.includes('network') || siteChangeError.includes('fetch')) {
        return 'Network error. Please check your connection and try again.';
      }
      if (siteChangeError.includes('timeout')) {
        return 'Request timed out. Please try again.';
      }
      if (siteChangeError.includes('not found') || siteChangeError.includes('404')) {
        return 'Site not found. Please verify the site code and try again.';
      }
      return siteChangeError;
    }
    return null;
  }, [localValidationError, siteChangeError]);

  // Handle site input key press
  const handleSiteInputKeyPress = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSiteInputSubmit();
    }
    // Note: Escape key functionality removed as per simplified spec
    // Input will be disabled during site change process instead
  }, [handleSiteInputSubmit]);

  // Load equipment hierarchy when component mounts
  useEffect(() => {
    // This effect is intentionally left empty as the hierarchy
    // is loaded by the parent component using useEquipmentInitialization
  }, []);

  return (
    <Column spacing="300" height="100%" heights={["fit", "fit", "fit"]}>
      {/* Compact site header */}
      <Row spacing="200" alignmentVertical="center">
        <Heading level={6}>{currentSite || site}</Heading>
        {siteChangeInProgress && <Loader size="small" />}
      </Row>

      {/* Consolidated controls */}
      <Column spacing="200">
        <Input
          type="text"
          value={siteInputValue}
          onChange={handleSiteInputChange}
          onBlur={handleSiteInputBlur}
          onKeyDown={handleSiteInputKeyPress}
          placeholder="Change site..."
          size="small"
          disabled={siteChangeInProgress || isLoading.hierarchy}
          error={!!(localValidationError || siteChangeError)}
          errorMessage={getErrorMessage() || undefined}
        />
        
        <SearchField
          value={query}
          onChange={setQuery}
          placeholder="Search equipment..."
          onSubmit={onSubmit}
          size="small"
        />

        {/*status messages */}
        {siteChangeSuccess && (
          <Column alignmentHorizontal="center">
            <Text type="b100" color="success">
              Site changed!
            </Text>
          </Column>
        )}
      </Column>

      {/* Loading state */}
      {isLoading.hierarchy && (
        <Column alignmentHorizontal="center" spacingInset="400" spacing="200">
          <Loader size="medium" />
          <Text type="b200" color="secondary">
            {siteChangeInProgress ? 'Loading new site data...' : 'Loading equipment data...'}
          </Text>
        </Column>
      )}

      {/* Error state */}
      {errors.hierarchy && (
        <Column alignmentHorizontal="center" spacingInset="400">
          <Text type="b200" color="error">{errors.hierarchy}</Text>
        </Column>
      )}

      {/* Tree structure */}
      {!isLoading.hierarchy && !errors.hierarchy && (
        <EquipmentTreeSideMenu
          equipmentHierarchy={equipmentHierarchy}
          selectedNodeId={selectedNodeId}
          onNodeSelect={onNodeSelect}
          searchTerm={query}
        />
      )}
    </Column>
  );
};
